import { matchesDepartment } from './departments';
import { addDays, diffDays, isoWeek, mondayOf } from './helpers';
import { ActivityProjection, ScheduleStatus, Timeline, WorkStatus } from './types';

export type TimelineZoom = 'week' | 'month' | 'quarter';
export type TimelineFilter = 'all' | 'open' | 'mine';

export const TIMELINE_LABEL_WIDTH = 280;
export const zoomPxPerDay: Record<TimelineZoom, number> = { week: 32, month: 14, quarter: 5 };

export interface TimelineWindow {
  start: string;
  end: string;
  days: number;
}

const longMonthNames = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];

const timelineDates = (timeline: Timeline): string[] => [
  ...timeline.announcements.map((item) => item.date),
  ...timeline.rows.flatMap((row) => row.points.flatMap((point) => [
    point.baseline,
    ...(point.forecast ? [point.forecast] : []),
    ...(point.duration ? [point.duration.start, point.duration.end] : []),
  ])),
].sort();

/** Standardfönstret är idag −14 dagar till senaste avisering +28 dagar. Kvartal visar hela planen. */
export const timelineWindow = (timeline: Timeline, zoom: TimelineZoom): TimelineWindow => {
  const latestAnnouncement = timeline.announcements.map((item) => item.date).sort().pop() || timeline.asOf;
  let start = addDays(timeline.asOf, -14);
  let end = addDays(latestAnnouncement, 28);
  if (end < addDays(timeline.asOf, 28)) end = addDays(timeline.asOf, 28);
  if (zoom === 'quarter') {
    const dates = timelineDates(timeline);
    if (dates[0] && dates[0] < start) start = addDays(dates[0], -7);
    const last = dates[dates.length - 1];
    if (last && addDays(last, 7) > end) end = addDays(last, 7);
  }
  start = mondayOf(start);
  end = addDays(mondayOf(end), 6);
  return { start, end, days: diffDays(end, start) + 1 };
};

export type Placement =
  | { inside: true; day: number }
  | { inside: false; side: 'before' | 'after' };

export const placeDate = (date: string, window: TimelineWindow): Placement => {
  if (date < window.start) return { inside: false, side: 'before' };
  if (date > window.end) return { inside: false, side: 'after' };
  return { inside: true, day: diffDays(date, window.start) };
};

/** Mittpunkten av dagen i pixlar från plottens vänsterkant. */
export const dayCenter = (day: number, pxPerDay: number): number => day * pxPerDay + pxPerDay / 2;

export interface AxisMonth {
  key: string;
  label: string;
  startDay: number;
  days: number;
}

export interface AxisWeek {
  key: string;
  week: number;
  startDay: number;
  days: number;
  relative: number | null;
}

export const buildAxis = (window: TimelineWindow, announcement?: string | null): { months: AxisMonth[]; weeks: AxisWeek[] } => {
  const months: AxisMonth[] = [];
  let cursor = window.start;
  while (cursor <= window.end) {
    const year = Number(cursor.slice(0, 4));
    const month = Number(cursor.slice(5, 7));
    const nextMonth = month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const last = nextMonth > window.end ? addDays(window.end, 1) : nextMonth;
    months.push({
      key: cursor.slice(0, 7),
      label: months.length === 0 || month === 1 ? `${longMonthNames[month - 1]} ${year}` : longMonthNames[month - 1],
      startDay: diffDays(cursor, window.start),
      days: diffDays(last, cursor),
    });
    cursor = nextMonth;
  }
  const anchor = announcement ? mondayOf(announcement) : null;
  const weeks: AxisWeek[] = [];
  for (let weekStart = window.start; weekStart <= window.end; weekStart = addDays(weekStart, 7)) {
    const startDay = diffDays(weekStart, window.start);
    weeks.push({
      key: weekStart,
      week: isoWeek(weekStart),
      startDay,
      days: Math.min(7, window.days - startDay),
      relative: anchor ? Math.round(diffDays(weekStart, anchor) / 7) : null,
    });
  }
  return { months, weeks };
};

export interface TimelineItem {
  id: string;
  title: string;
  phaseId: string;
  baseline: string;
  forecast: string | null;
  duration: { start: string; end: string } | null;
  labels: { chainId: string; label: string }[];
  status: ScheduleStatus;
  workStatus: WorkStatus | null;
  assigneeUserId: string | null;
  roleText: string;
}

export interface TimelineGroup {
  phaseId: string;
  phaseName: string;
  items: TimelineItem[];
  totalCount: number;
  doneCount: number;
  lateCount: number;
  openCount: number;
}

export const buildTimelineGroups = (
  timeline: Timeline,
  activities: ActivityProjection[] = [],
  filter: TimelineFilter = 'all',
  currentUserId = '',
  departments: readonly string[] = [],
): TimelineGroup[] => {
  const byId = new Map(activities.map((activity) => [activity.id, activity]));
  return timeline.rows.map((row) => {
    const all: TimelineItem[] = row.points.map((point) => {
      const activity = byId.get(point.id);
      return {
        ...point,
        status: activity?.scheduleStatus || (point.baseline < timeline.asOf ? 'overdue' : 'on_track'),
        workStatus: activity?.workStatus || null,
        assigneeUserId: activity?.assigneeUserId || null,
        roleText: activity?.roleText || '',
      };
    }).sort((left, right) => left.baseline.localeCompare(right.baseline) || left.title.localeCompare(right.title, 'sv'));
    const items = all.filter((item) => {
      if (filter === 'open') return item.status !== 'complete';
      if (filter === 'mine') {
        return (Boolean(currentUserId) && item.assigneeUserId === currentUserId) || matchesDepartment(item.roleText, departments);
      }
      return true;
    });
    return {
      phaseId: row.phaseId,
      phaseName: row.phaseName,
      items,
      totalCount: all.length,
      doneCount: all.filter((item) => item.status === 'complete').length,
      lateCount: all.filter((item) => item.status === 'overdue').length,
      openCount: all.filter((item) => item.status !== 'complete').length,
    };
  });
};
